import store from '@/store';

/**
 * Default notification duration in milliseconds
 */
const DEFAULT_DURATION = 5000;

/**
 * Create and display a notification
 * 
 * @param {string} type - The type of notification ('success', 'error', 'warning', 'info')
 * @param {string} message - The message to display
 * @param {Object} options - Additional options
 * @param {string} options.title - Optional title for the notification
 * @param {number} options.duration - Duration in milliseconds (0 for persistent)
 * @returns {string} The ID of the created notification
 */
export function notify(type, message, options = {}) {
  // Validate type
  const validTypes = ['success', 'error', 'warning', 'info'];
  if (!validTypes.includes(type)) {
    console.warn(`Invalid notification type: ${type}. Using 'info' instead.`);
    type = 'info';
  }
  
  // Create notification object
  const notification = {
    id: generateId(),
    type,
    message,
    title: options.title || '',
    duration: options.duration !== undefined ? options.duration : DEFAULT_DURATION,
    timestamp: Date.now()
  };
  
  // Add to store
  store.dispatch('notifications/add', notification);
  
  return notification.id;
}

/**
 * Show a success notification
 * 
 * @param {string} message - The message to display
 * @param {Object} options - Additional options
 * @returns {string} The ID of the created notification
 */
export function notifySuccess(message, options = {}) {
  return notify('success', message, options);
}

/**
 * Show an error notification
 * 
 * @param {string} message - The message to display
 * @param {Object} options - Additional options
 * @returns {string} The ID of the created notification
 */
export function notifyError(message, options = {}) {
  return notify('error', message, options);
}

/**
 * Show a warning notification
 * 
 * @param {string} message - The message to display
 * @param {Object} options - Additional options
 * @returns {string} The ID of the created notification
 */
export function notifyWarning(message, options = {}) {
  return notify('warning', message, options);
}

/**
 * Show an info notification
 * 
 * @param {string} message - The message to display
 * @param {Object} options - Additional options
 * @returns {string} The ID of the created notification
 */
export function notifyInfo(message, options = {}) {
  return notify('info', message, options);
}

/**
 * Remove a notification by ID
 * 
 * @param {string} id - The ID of the notification to remove
 */
export function removeNotification(id) {
  store.dispatch('notifications/remove', id);
}

/**
 * Remove all notifications
 */
export function clearAllNotifications() {
  store.dispatch('notifications/clearAll');
}

/**
 * Generate a unique ID for notifications
 * 
 * @returns {string} A unique ID
 */
function generateId() {
  return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Default export with all functions
export default {
  notify,
  notifySuccess,
  notifyError,
  notifyWarning,
  notifyInfo,
  removeNotification,
  clearAllNotifications
};